// ═══════════════════════════════════════════════════════════════
// control/control-io.ts — the browser-safe control-plane construction
// ═══════════════════════════════════════════════════════════════
//
// The control plane composes the 10 PURE subsystems of the engine —
// the ones that need no CryptoHandle, no secrets, no node:* — into one
// in-process lens set a browser can run without a round-trip:
//
//   policy · permissions · validation · web · threats ·
//   compliance · analysis · network · runtime · agentic
//
// Browser-safety (Principle 1) is real, not nominal:
//   • each subsystem is imported from its OWN engine subpath
//     (`@liquidefy/security/policy`, …) — the engine is entirely
//     node-free (it takes an INJECTED node:crypto for the Node handle,
//     never imports node:*), so none of these pull node into a bundle;
//   • this module imports NOTHING from the execution plane (no
//     security-root, no seams, no stores, no KEK, no stateful adapters);
//   • the subsystem factories use the engine's eagerly-initialized
//     default seams (system clock/idgen), so no `createSecurityIo` and
//     no CryptoHandle is needed to construct them.
//
// The strengthened two-plane lint (devkit/verify.ts) machine-checks all
// of the above.

import { createPolicyIo, type PolicyIo } from "@liquidefy/security/policy";
import { createPermissionsIo, type PermissionsIo } from "@liquidefy/security/permissions";
import { createValidationIo, type ValidationIo } from "@liquidefy/security/validation";
import { createWebIo, type WebIo } from "@liquidefy/security/web";
import { createThreatIo, type ThreatIo } from "@liquidefy/security/threats";
import { createComplianceIo, type ComplianceIo } from "@liquidefy/security/compliance";
import { createAnalysisIo, type AnalysisIo } from "@liquidefy/security/analysis";
import { createNetworkIo, type NetworkIo } from "@liquidefy/security/network";
import { createRuntimeIo, type RuntimeIo } from "@liquidefy/security/runtime";
import { createAgenticIo, type AgenticIo } from "@liquidefy/security/agentic";
import { capabilities } from "@liquidefy/security";
import type { NetworkSecurityControl } from "../network-security-io.js";

// Re-export the lens IO types so consumers can name them from one place.
export type {
  PolicyIo,
  PermissionsIo,
  ValidationIo,
  WebIo,
  ThreatIo,
  ComplianceIo,
  AnalysisIo,
  NetworkIo,
  RuntimeIo,
  AgenticIo,
};

/**
 * Construct the browser-safe control plane. Pure: no CryptoHandle, no
 * secrets, no execution-plane code. Safe to call in a browser bundle.
 *
 * In a server process where the execution plane also runs,
 * `createSecurityIo` will already have installed seeded clock/rng/idgen
 * globally, so these lenses pick them up; in a browser the engine's
 * eager system-seam defaults apply.
 */
export function createNetworkSecurityControl(): NetworkSecurityControl {
  return Object.freeze({
    capabilities,
    policy: createPolicyIo(),
    permissions: createPermissionsIo(),
    validation: createValidationIo(),
    web: createWebIo(),
    threats: createThreatIo(),
    compliance: createComplianceIo(),
    analysis: createAnalysisIo(),
    network: createNetworkIo(),
    runtime: createRuntimeIo(),
    agentic: createAgenticIo(),
  });
}
